import { OnInit, OnDestroy, Injectable } from "@angular/core";
import { WorldPayStompService } from "./worldpay-stomp.service";
import { IMessage } from "@stomp/rx-stomp";
import { Observable, ReplaySubject, Subscription, catchError, map, of, take } from "rxjs";
import { PayPoint } from "src/app/shared/model/PayPoint.model";
import { ConstantsService } from "../constants.service";
import { UntilDestroy } from "@ngneat/until-destroy";
import { SessionContextService } from "../session-context.service";
import { APIResult, WebAPIResult } from "../employee.service";
import { HttpClient } from "@angular/common/http";
import { Stomp } from "../stomp";
import { StompJs } from "../stomp.umd.js";

@UntilDestroy({ checkProperties: true})
@Injectable({
  providedIn: 'root',
})
export class CardPaymentService implements OnInit, OnDestroy{
  constructor(private worldPayStompService: WorldPayStompService, private constants: ConstantsService, private session: SessionContextService, private http: HttpClient){}

  registration$: Subscription;
  refresh$: Subscription;
  notification$: Subscription;
  receipt$: Subscription;
  result$: Subscription;
  action$: Subscription;
  complete$: Subscription;
  error$: Subscription;

  onRegistration: ReplaySubject<string> = new ReplaySubject(1,100);
  onNotification: ReplaySubject<string> = new ReplaySubject(1,100);
  onError: ReplaySubject<string> = new ReplaySubject(1,100);
  onResult: ReplaySubject<string> = new ReplaySubject(1,100);

  payPoint: PayPoint;

  ngOnInit(){

    // handle registration (reply)
    this.registration$ = this.worldPayStompService.watch("/client/v1/pos/registration").subscribe((message: IMessage) => {
      ;
      var payload = JSON.parse(message.body);
      var headers = message.headers;
      this.handleRegistration(payload?.pointOfSaleLicenseKey);
    });

    // handle registration refresh (reply)
    this.refresh$ = this.worldPayStompService.watch("/client/v1/pos/registration/refresh").subscribe((message: IMessage) => {
      ;
      var payload = JSON.parse(message.body);
      var headers = message.headers;
      this.handleRegistration(payload?.pointOfSaleLicenseKey);
    });

    // handle notification
    this.notification$ = this.worldPayStompService.watch('/client/v1/payment/notification').subscribe((message: IMessage) => {
      ;
      var payload = JSON.parse(message.body);
      var headers = message.headers;

      if (payload?.notificationText){
        // return the notification text to the user
        this.onNotification.next(payload.notificationText);
      }
    });

    // handle receipt
    this.receipt$ = this.worldPayStompService.watch('/client/v1/payment/receipt').subscribe((message: IMessage) => {
      var payload = JSON.parse(message.body);
      var headers = message.headers;

      // print the receipt
      // Note to future self - agreed with LG that, for now at least, there is no requirement to handle the receipt
    });

    // handle result
    this.result$ = this.worldPayStompService.watch('/client/v1/payment/result').subscribe((message: IMessage) => {
      ;
      var payload = JSON.parse(message.body);
      var headers = message.headers;

      // Handle the result of the payment, eg display on screen, or store the payment data
      this.onResult.next(payload);
    });

    // handle action
    this.action$ = this.worldPayStompService.watch('/client/v1/payment/action').subscribe((message: IMessage) => {
      ;
      var payload = JSON.parse(message.body);
      var headers = message.headers;

      // perform the action

      // respond to the action be sending a message to /v1/payment/action
    });

    // handle complete
    this.complete$ = this.worldPayStompService.watch('/client/v1/payment/complete').subscribe((message: IMessage) => {
      ;
      var payload = JSON.parse(message.body);
      var headers = message.headers;

      // the payment flow has completed, ready to start the next payment

      // disconnect from IPS Cloud
      // this.worldPayStompService.deactivate();
    });

    // handle error
    this.error$ = this.worldPayStompService.watch("/client/v1/error").subscribe((message: IMessage) => {
      ;
      var payload = JSON.parse(message.body);
      var headers = message.headers;
      this.onError.next(message.body);
      // TODO: correct the error

      // disconnect from IPS Cloud
      this.worldPayStompService.deactivate();

      // TODO:  Retry if necessary
    });
  }

  ngOnDestroy(){
    this.registration$?.unsubscribe();
    this.refresh$?.unsubscribe();
    this.notification$?.unsubscribe();
    this.receipt$?.unsubscribe();
    this.result$?.unsubscribe();
    this.action$?.unsubscribe();
    this.complete$?.unsubscribe();
    this.error$?.unsubscribe();
  }

  get hasLicenceKey(){
    return (this.payPoint && this.payPoint.LicenceKey && (this.payPoint.LastRegistered>new Date()))
  }

  generateReference() {
    // using a part of Plancks constant to multiply the randomise number
    const uniqueGeneratorOne = Math.floor(1000 + Math.random() * 6626).toString();
    const uniqueGeneratorTwo = Math.floor(1000 + Math.random() * 7015).toString();
    const uniqueGeneratorThree = Math.floor(1000 + Math.random() * 6626).toString();
    return uniqueGeneratorOne + '-' + uniqueGeneratorTwo + '-' + uniqueGeneratorThree;
  }

  makeCardPayment(payPoint: PayPoint, operationId: string, transactionReference: string, value: number){

    // Check we have valid params before processing...
    if (!(payPoint && operationId)){
      this.onError.next('Unable to identify the Pay Point. Payment has been aborted.');
      return;
    }
    if (!(transactionReference && value)){
      this.onError.next('Payment details missing. Payment has been aborted.');
      return;
    }

    // stash the paypoint in case we need it later
    this.payPoint = payPoint;

    // Define the payload for starting a payment
    var payload = JSON.stringify({
      paymentType: 'sale',
      merchantTransactionReference: transactionReference,
      instruction: {
        value: {
          amount: value
        },
        paymentInstrument: {
          type: "card/present"
        }
      }
    })

    // Define the headers
    var headers = {
      'x-wp-publisher-id': payPoint.UniqueId,   // Unique ID per POS instance
      'x-wp-correlation-id': operationId, // UUID, unique per operation
      'x-wp-message-id': this.constants.uuidv4(),     // UUID, unique per message
    }

    // //establish a connection
    // this.connect();

    // // start payment
    // this.worldPayStompService.publish({destination: '/v1/payment', headers: headers, body: payload});

    // TODO:  Temporarily just call onResult to allow testing of UI.  Remove before release...
    this.onResult.next('');
  }

  register(payPoint: PayPoint, operationId: string){
    
    // Check we have valid params before processing...
    if (!(payPoint && operationId)){
      this.onError.next('Unable to identify the Pay Point. Pay Point registration has been unsuccessful.');
      return;
    }

    this.testRegister(payPoint, operationId);
    return;

    // stash the paypoint so we can update later if we get a suitable response...
    this.payPoint = payPoint;

    // var payload = JSON.stringify({
    //   pointOfSaleReference: 'ActiveSalonTest',      // The Point of Sale Reference you entered into the IPS Cloud Control Center
    //   pointOfSaleId: 'bEzBkI',              // Point of Sale ID from the IPS Cloud Control Center
    //   pointOfSaleActivationCode: '3Z7pfvrU' // Point of Sale Activation Code from the IPS Cloud Control Center
    // })

    // Define the headers
    var headers = {
      'x-wp-publisher-id': payPoint.UniqueId,   // Unique ID per POS instance
      'x-wp-correlation-id': operationId, // UUID, unique per operation
      'x-wp-message-id': this.constants.uuidv4(),     // UUID, unique per message
    }

    ;

    // establish connection
    this.connect();

    // wait a second before attempting publish - this gives the connection a chance to be established
    setTimeout(() => {

      if (this.hasLicenceKey){
        this.worldPayStompService.publish({destination: "/v1/pos/registration/refresh", headers: headers, body: undefined});
      }
      else{
        // Define the payload for registration
        // https://ips-controlcenter.wpt-test.worldpaypp.com/pos-activation
        var payload = JSON.stringify({
          pointOfSaleReference: payPoint.Reference,  // The Point of Sale Reference entered into the IPS Cloud Control Center
          pointOfSaleId: payPoint.PayPointId,  // Point of Sale ID from the IPS Cloud Control Center
          pointOfSaleActivationCode: payPoint.ActivationCode, // Point of Sale Activation Code from the IPS Cloud Control Center
        });

        this.worldPayStompService.publish({destination: "/v1/pos/registration", headers: headers, body: payload});
        
        // TODO: Remove after demo...
        // this.handleRegistration("123");
      }

    }, 1000);
  }

  private connect(){
    // check connection has been established activate if/as necessary ready to publish
    if ((this.payPoint)){// && (!this.worldPayStompService.connected())){
      // const connectHeaders = { 'x-wp-authorization': this.payPoint.LicenceKey };
      // const connectHeaders = this.hasLicenceKey ? { 'x-wp-authorization': this.payPoint.LicenceKey } : {};
      // const url = this.session.defaultData.defData.IPSCloudUrl + this.payPoint.Reference;
      const url = "wss://ws.muat.worldpaypp.com:443/ipc-app/payment/ActiveSalonTest";

      // defaults already set in factory, calling configure here will add to existing config
      this.worldPayStompService.configure({
        brokerURL: url,
        connectHeaders: {},
      });

      this.worldPayStompService.webSocketErrors$.subscribe((error) => {
        ;
        console.log(new Date(), error);
      });

      try{
        this.worldPayStompService.deactivate().then(() => {
          // connect to IPS Cloud
          this.worldPayStompService.activate();
        })
      }
      catch{}
    }
  }

  private handleRegistration(pointOfSaleLicenseKey: string){
    if (this.payPoint){
      // Store the POS license key in the payload, to be used in the x-wp-authorisation header of future connections to IPS Cloud
      if (pointOfSaleLicenseKey){

        ;
        
        // set the licence key
        this.payPoint.LicenceKey = pointOfSaleLicenseKey;

        // Set Last Registered Date
        this.payPoint.LastRegistered = new Date();

        // save changes to the PayPoint...
        this.savePayPoint(this.payPoint).subscribe((result) => {
          if (result.Succeeded){
            // raise notifiction indicating success
            this.onRegistration.next('Pay Point has been successfully registered');
          }
          else{
            // save not successful, raise error indicating failed to update pay point
            this.onError.next('Failed to update Pay Point: ' + result.Msg);
          }
        });
      }
      else{
        // we have a pay point but licence key not specified in the response, raise error
        this.onError.next('Licence key not found.  Registration unsuccessful');
      }

      // disconnect from IPS Cloud
      this.worldPayStompService.deactivate();
    }
  }

  private savePayPoint(payPoint: PayPoint): Observable<APIResult>{
    if (!payPoint) {
      this.onError.next('Pay Point could not be updated');
      return of(undefined);
    }
        
    return this.http.put<WebAPIResult>('/api/BackOffice/PayPoint', payPoint).pipe(
      take(1),
      // tap(d => {
      //     console.log(d);
      // }),
      catchError((e) => {
        const result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e.Message;
        return of(result);
      }),
      map((m) => {
        const result: APIResult = new APIResult();
        result.Success = 1;
        return result;
      }));
  }

  private testRegister(payPoint: PayPoint, operationId: string){
    // var stompClient = Stomp.client("wss://ws.muat.worldpaypp.com/ipc-app/payment/ActiveSalonTest");
    ;
    var stompClient = Stomp.client("wss://ws.muat.worldpaypp.com:443/ipc-app/payment/ActiveSalonTest");

    // Function to handle the connection errors with IPS Cloud
    var handle_connection_error = function(error) {
      // Correct the error and retry
      ;
      console.log(new Date(), error);
    }

    // Function to handle a successful registration reply
    var handle_registration_reply = function(stompMessage) {
      var payload = JSON.parse(stompMessage.body)
      var headers = stompMessage.headers

      // Store the POS License Key in the payload, to be used in the x-wp-authorisation header of future connections to IPS Cloud
      ; 

      // Disconnect from IPS Cloud
      stompClient.disconnect(null, handle_connection_error)
    }

    // Function to handle errors from an unsuccessful registration
    var handle_error_reply = function(stompMessage) {
      var payload = JSON.parse(stompMessage.body)
      var headers = stompMessage.headers

      // Correct the error
      ; 

      // Disconnect from IPS Cloud
      stompClient.disconnect(null, handle_connection_error)

    }

    var start_registration = function() {
      ;
      // Subscribe to destinations you expect to receive replies on, assigning functions to each reply
      stompClient.subscribe('/client/v1/pos/registration', handle_registration_reply)
      stompClient.subscribe('/client/v1/error', handle_error_reply)

      var headers = {
        'x-wp-publisher-id': payPoint.UniqueId,   // Unique ID per POS instance
        'x-wp-correlation-id': operationId, // UUID, unique per operation
        'x-wp-message-id': this.constants.uuidv4(),    // UUID, unique per message
      }

      // Define the payload for registration
      var payload = JSON.stringify({
        pointOfSaleReference: payPoint.Reference,  // The Point of Sale Reference entered into the IPS Cloud Control Center
        pointOfSaleId: payPoint.PayPointId,  // Point of Sale ID from the IPS Cloud Control Center
        pointOfSaleActivationCode: payPoint.ActivationCode, // Point of Sale Activation Code from the IPS Cloud Control Center
      });

      // Start the registration
      stompClient.send('/v1/pos/registration', headers, payload);
    }

    var headers = {};

    // Connect to IPS Cloud, and start the registration when connected
    stompClient.connect(headers, start_registration, handle_connection_error);
  }

  // private testRegister2(payPoint: PayPoint, operationId: string){
  //   let stompClient;
  //   const stompConfig = {
  //     // Typically, login, passcode and vhost
  //     // Adjust these for your broker
  //     connectHeaders: {},

  //     // Broker URL, should start with ws:// or wss:// - adjust for your broker setup
  //     brokerURL: "wss://ws.muat.worldpaypp.com/ipc-app/payment/ActiveSalonTest",

  //     // Keep it off for production, it can be quit verbose
  //     // Skip this key to disable
  //     debug: function (str) {
  //       console.log('STOMP: ' + str);
  //     },

  //     // If disconnected, it will retry after 200ms
  //     reconnectDelay: 500,

  //     // Subscriptions should be done inside onConnect as those need to reinstated when the broker reconnects
  //     onConnect: function (frame) {
  //       // The return object has a method called `unsubscribe`
  //       // 
  //       ;
  //       stompClient.deactivate();
  //     }
  //   };

  //   ;

  //   stompClient = new StompJs.Client(stompConfig);

  //   // Attempt to connect
  //   stompClient.activate();
  // }
}